import { Component } from '@theme/component';

/**
 * Header announcement bar.
 *
 * Hides itself while a dismissal cooldown is active and starts the cooldown
 * when the close button is clicked (`on:click="/dismiss"`). The cooldown
 * length in hours comes from data-dismiss-hours.
 *
 * @attr {string} data-dismiss-hours - Hours to stay hidden after dismissal (default 48)
 *
 * Also exposes its rendered height as --announcement-height on :root (0 when
 * hidden/dismissed), so content under a transparent header can start below
 * the bar (e.g. the email signup hero).
 *
 * @extends {Component<{}>}
 */
class HeaderAnnouncement extends Component {
	static STORAGE_KEY = 'header-announcement:hide-until';

	/** @type {ResizeObserver | null} */
	#resizeObserver = null;

	connectedCallback() {
		super.connectedCallback();

		const hideUntil = Number(window.localStorage.getItem(HeaderAnnouncement.STORAGE_KEY) ?? 0);
		if (hideUntil > Date.now()) this.hidden = true;

		// Fires on dismissal (hidden → 0 height) and breakpoint changes too.
		this.#resizeObserver = new ResizeObserver(() => this.#publishHeight());
		this.#resizeObserver.observe(this);
		this.#publishHeight();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#resizeObserver?.disconnect();
		this.#resizeObserver = null;
	}

	#publishHeight() {
		document.documentElement.style.setProperty('--announcement-height', `${this.offsetHeight}px`);
	}

	dismiss() {
		const hours = Number(this.dataset.dismissHours) || 48;
		window.localStorage.setItem(HeaderAnnouncement.STORAGE_KEY, String(Date.now() + hours * 60 * 60 * 1000));
		this.hidden = true;
	}
}

if (!customElements.get('header-announcement')) {
	customElements.define('header-announcement', HeaderAnnouncement);
}
